import { ChartTooltipTotal } from "@similarweb/ui-components";
import type { IChartDataAll } from "app/pages/website-analysis/audience-overlap/OverlapOverTime/types";
import { EMetricType } from "app/pages/website-analysis/audience-overlap/OverlapOverTime/types";
import type { FC } from "react";
import { i18nFilter } from "app/filters/i18nFilter";
import { minVisitsAbbrFilter, percentageSignFilter } from "app/filters/ngFilters";
import { colorsPalettes } from "@similarweb/styles";
import dayjs from "dayjs";
import styled from "styled-components";

interface ITooltipProps {
    chartDataAll: IChartDataAll;
    hoverPoint: { x: number; y: number };
}

const { TOTAL, PER_SITE } = EMetricType;

const Container = styled.div`
    width: 265px;
    white-space: pre-wrap;
`;

const Header = styled.span`
    line-height: 20px;
`;

const Display = styled.span<{ withOpacity?: boolean }>`
    ${({ withOpacity }) => withOpacity && `opacity: 0.4`};
`;

const VISITS_THRESHOLD = 5000;

export const Tooltip: FC<ITooltipProps> = ({ hoverPoint, chartDataAll }) => {
    const i18n = i18nFilter();
    const chartDataPerSite = chartDataAll[PER_SITE];
    const chartDataTotal = chartDataAll[TOTAL];
    const headers = [
        i18n("overlap.overtime.chart.tooltip.domain"),
        <Header key={"unique"}>{i18n("overlap.overtime.chart.tooltip.domain.unique.visitors")}</Header>,
        <Header key={"shared"}>{i18n("overlap.overtime.chart.tooltip.domain.shared.audience")}</Header>,
    ];
    const title = dayjs.utc(hoverPoint.x).format("MMMM YYYY").toString();
    const rows = chartDataPerSite.map((chartDataItem) => {
        const { name, color, data, isSuggested } = chartDataItem;
        const relevantPoint = data.find((a) => a.x === hoverPoint.x);
        return {
            displayName: <Display withOpacity={isSuggested}>{name}</Display>,
            firstValue: (
                <Display withOpacity={isSuggested}>
                    {minVisitsAbbrFilter()(relevantPoint.uniqueUsers, VISITS_THRESHOLD)}
                </Display>
            ),
            secondValue: (
                <Display withOpacity={isSuggested}>
                    {relevantPoint.uniqueUsers > VISITS_THRESHOLD ? percentageSignFilter()(relevantPoint.y, 2) : "N/A"}
                </Display>
            ),
            color,
        };
    });
    const total = {
        displayName: i18n("overlap.overtime.chart.tooltip.total"),
        secondValue: minVisitsAbbrFilter()(chartDataTotal[0].data.find(({ x }) => x === hoverPoint.x).y),
        color: colorsPalettes.blue[400],
    };
    return (
        <Container>
            <ChartTooltipTotal
                total={total}
                rows={rows}
                headers={headers}
                title={title}
            />
        </Container>
    );
};
